import { Mui, MuiIcons } from "@osu/react-ui";
import { useSelector, useStore } from "react-redux";
import { selectDocumentsData, openAllDocuments, resetOpenAllDocuments } from "../../services/graphql/slices/applicant-detail-slices/documents-slice";
import { useLazyGetDocumentsQuery, useSearchDocumentsQuery } from "../../services/graphql/api";
import { DOCUMENTTYPE_TO_KEYWORDNAMES } from "../../util/constants";
import { getApplDetailParams } from "../../util/functions";

const classes = {
    list: { textAlign: "right", width: "95%", },
    listItem: { padding: 0, },
    title: { width: "100%", },
    loadingIndicator: { marginLeft: "2rem" },
    loadingIndicatorOpenAll: { marginRight: "0.5rem" },
    bottomButton: { display: "flex", justifyContent: "flex-start", marginTop: "1rem" },
    errorListing: { color: 'red', fontWeight: "bold", marginLeft: "1rem" },
    errorLabel: { display: "flex", fontStyle: "italic", justifyContent: "flex-start" },
    errorLabelOpenAll: { display: "flex", fontStyle: "italic", justifyContent: "flex-start", marginLeft: "1rem", marginTop: "0.5rem" },
};

export default function Documents() {
    const { applicantEmployeeId: emplid, programId } = getApplDetailParams();
    // Guard the NonNull emplid query (mirrors the other applicant-detail sections): without this the
    // query fires with emplid=undefined before the route resolves, and AppSync rejects it with
    // "Variable 'emplid' has coerced Null value for NonNull type 'String!'".
    const { data, error, isFetching, refetch } = useSearchDocumentsQuery(
        { emplid, programId },
        { skip: !emplid || !programId, refetchOnMountOrArgChange: true }
    );
    
    const transformedDocs = isFetching ? [] : transformDocumentList(data?.data);
    const hasNoDocuments = !isFetching && !error && !(transformedDocs?.length > 0);

    function transformDocumentList(documents) {
        if (!Array.isArray(documents)) { return documents; }

        let state = [];

        for (let doc of documents) {
            let newItem = {
                documentType: doc.documentType.split('ADM')[1]?.trim() ?? doc.documentType, // Primarily needed for the Application document
                documentId: doc.documentID,
                documentDate: new Date(doc.documentDate).toLocaleDateString(),
                documentDateOriginal: doc.documentDate,
                documentName: doc.documentName,
                orgName: doc.keywords.find((keyword) => {
                    return keyword.keywordName === "Org Name"
                })?.keywordValue
            };

            newItem.displayLabel = newItem.documentType;

            if (newItem.orgName && newItem.documentType.includes("University Document")) {
                newItem.documentType += " - " + newItem.orgName;
            }

            // Set display labels from keyword
            let keywordNames = DOCUMENTTYPE_TO_KEYWORDNAMES[doc.documentType];

            if (Array.isArray(keywordNames)) {
                keywordNames.forEach(keywordName => {
                    const keyword = doc.keywords.find(keyword => keyword.keywordName === keywordName);
                    if (keyword) {
                        newItem.keyword = keyword.keywordValue;
                        newItem.displayLabel = keyword.keywordValue;
                    }
                });
            }

            newItem.sortOrder = getSortOrder(newItem.displayLabel?.toUpperCase?.() ?? "");
            state.push(newItem);
        }

        state.sort((doc1, doc2) => {
            if (doc1.sortOrder < doc2.sortOrder) { return -1; }
            if (doc1.sortOrder > doc2.sortOrder) { return 1; }
            if (doc1.documentType < doc2.documentType) return -1;
            if (doc1.documentType > doc2.documentType) return 1;
            if (doc1.documentDateOriginal > doc2.documentDateOriginal) return -1;
            if (doc1.documentDateOriginal < doc2.documentDateOriginal) return 1;
            return 0;
        });

        return state;
    }

    function getSortOrder(displayLabel) {
        const order = {
            "SCHOOL REPORT": 0,
            "HS PROFILE": 1,
            "HS TRANSCRIPT": 2,
            "UNIVERSITY TRANSCRIPT": 3,
            "APPLICATION": 4,
            "RECOMMENDATION": 5,
        };

        return order[displayLabel] ?? 1000; // Shove to the end
    }

    return (
        <Mui.List sx={classes.list}>
            <LoadingIndicator isFetching={isFetching} />

            <NoDocumentsFound showMessage={hasNoDocuments} />

            <ListingErrorRefreshButton isFetching={isFetching} isError={!!error} refetch={refetch} />

            <DocumentsListing isFetching={isFetching} docs={transformedDocs} />
        </Mui.List>
    );
}

function LoadingIndicator({ isFetching }) {
    if (!isFetching) { return; }
    
    return (
        <>
            <Mui.Skeleton variant="rect" height="2em" width="100%"></Mui.Skeleton>
            <br />
            <Mui.Skeleton variant="rect" height="2em" width="100%"></Mui.Skeleton>
            <br />
            <Mui.Skeleton variant="rect" height="2em" width="100%"></Mui.Skeleton>
            <br />
            <Mui.Skeleton variant="rect" height="2em" width="100%"></Mui.Skeleton>
            <br />
            <Mui.Skeleton variant="rect" height="2em" width="100%"></Mui.Skeleton>
        </>
    );
}

function NoDocumentsFound({ showMessage }) {
    if (!showMessage) { return <></>; }

    return <Mui.Box style={{ textAlign: "left" }} tabindex="0">No documents found for this application.</Mui.Box>;
}

function ListingErrorRefreshButton({ isError, isFetching, refetch }) {
    if (!isError){ return <></>; }
    
    return (
        <Mui.Box style={{ textAlign: "left" }}>
            <Mui.Typography color="error" component="span" sx={classes.errorListing} variant="subtitle2">Error loading documents. Please try again.</Mui.Typography>
            
            <br/> <br/>
            
            <Mui.Button color="primary" disabled={isFetching} onClick={refetch} variant="contained"><MuiIcons.Refresh /></Mui.Button>
        </Mui.Box>
    );
}

function DocumentsListing({ docs, isFetching }) {
    return (
        <>
            {docs?.map?.((item, index) => (
                <div key={`${index}-${item.documentId}`}>
                    <Mui.ListItem sx={classes.listItem}>
                        <Mui.ListItemText sx={classes.title} primary={ <DownloadableDocumentLink document={item} /> } />

                        <Mui.ListItemText primary={item.documentDate} sx={classes.list} tabindex="0" />
                    </Mui.ListItem>
                    <Mui.Divider />
                </div>
            ))}

            <OpenAllDocumentsButton showButton={!isFetching} docs={docs} />
        </>
    );
}

function DownloadableDocumentLink({ document }) {
    const [getDocumentsTrigger, { isFetching, error }] = useLazyGetDocumentsQuery();
    const { pendingDownloadsByRequestedDocumentKey } = useSelector(selectDocumentsData);
    
    const { documentId, documentType: documentName, displayLabel } = document;
    const requestedDocumentKey = `${documentId}#${documentName}`;
    
    const isWaitingForEvent = !!pendingDownloadsByRequestedDocumentKey[requestedDocumentKey];
    const isFetchingOrWaiting = isFetching || isWaitingForEvent;
    const navbutton = { ":focus-visible": { "outline": "2pt solid #0060df", "border-radius": "2px" } };

    return (
        <Mui.Box style={{ display: 'flex', alignItems: 'center' }}>
            <Mui.Button color="primary" onClick={() => getDocumentsTrigger({ documentId, documentName })} sx={navbutton} title={documentId}>{displayLabel}</Mui.Button>
            
            {isFetchingOrWaiting && <Mui.CircularProgress size={20} style={classes.loadingIndicator} />}
            
            {error && <Mui.Typography sx={classes.errorLabel} variant="overline">Failed to Load</Mui.Typography>}
        </Mui.Box>
    );
}

function OpenAllDocumentsButton({ showButton, docs }) {
    const { dispatch } = useStore();
    const { isOpeningAll } = useSelector(selectDocumentsData);
    const [getDocument, { isLoading, error }] = useLazyGetDocumentsQuery();
    const isLoadingOrWaiting = isLoading || isOpeningAll;

    if (!showButton || !(docs?.length > 0)) { return <></>; }
    const payloads = docs.map(({ documentId, documentType: documentName }) => ({ documentId, documentName }));

    function makeApiCalls() {
        const openOrder = payloads.map(doc => `${doc.documentId}#${doc.documentName}`).reverse();
        const timeoutId = setTimeout(() => dispatch(resetOpenAllDocuments()), (60 * 1000));
        dispatch(openAllDocuments({ openOrder, timeoutId }));
        
        payloads.forEach(payload => getDocument(payload));
    }
    
    return (
        <Mui.Box style={{ display: 'flex', alignItems: 'center' }}>
            <Mui.Button disabled={isLoadingOrWaiting} onClick={makeApiCalls} style={classes.bottomButton} variant="outlined">
                <OpeningAllDocumentsSpinner isOpeningAll={isLoadingOrWaiting} />
                Open All
            </Mui.Button>

            {error && <Mui.Typography sx={ classes.errorLabelOpenAll} variant="overline">Failed to Load All</Mui.Typography>}
        </Mui.Box>
    )
}

function OpeningAllDocumentsSpinner({ isOpeningAll }) {
    if (!isOpeningAll) { return <></>; }

    return ( <Mui.CircularProgress size={20} style={classes.loadingIndicatorOpenAll} /> );
}
